import React, { useState, useEffect, useCallback, memo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import { storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useMemo } from "react";

const EditUser = memo(() => {
  const { id } = useParams();
  const { currentUser } = useSelector((state) => state.user);
  const [user, setUser] = useState({});
  const [branches, setBranches] = useState([]);
  const [branchesPage, setBranchesPage] = useState(1);
  const [branchesHasMore, setBranchesHasMore] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [minBetAmount, setMinBetAmount] = useState(10);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`/api/user/userEdit/${id}`);
      setUser(response.data);
      setMinBetAmount(response.data.minBetAmount || 10);
    } catch (err) {
      console.error("Fetch user error:", err);
      setError("Error fetching user details");
    }
  }, [id]);

  const fetchBranches = useCallback(async () => {
    if (!branchesHasMore || loadingBranches) return;

    setLoadingBranches(true);
    try {
      const response = await axios.get(
        `/api/branch/getbranch/${currentUser.username}?page=${branchesPage}&limit=100`
      );
      const { branches: newBranches, hasMore } = response.data;

      setBranches((prev) => [...prev, ...newBranches]);
      setBranchesHasMore(hasMore);
      setBranchesPage((prev) => prev + 1);
    } catch (err) {
      console.error("Fetch branches error:", err);
      setError("Error fetching branches");
    } finally {
      setLoadingBranches(false);
    }
  }, [currentUser.username, branchesPage, branchesHasMore, loadingBranches]);

  useEffect(() => {
    fetchUser();
    fetchBranches(); // Initial load
  }, [fetchUser]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleFileChange = useCallback((e) => {
    setImageFile(e.target.files[0]);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const updatedFields = { ...user, minBetAmount };
      
      if (!updatedFields.password) {
        delete updatedFields.password;
      }

      if (imageFile) {
        const userImageRef = ref(storage, `Images/${imageFile.name}`);
        await uploadBytes(userImageRef, imageFile);
        updatedFields.imageUrl = await getDownloadURL(userImageRef);
      }

      // First update the user details
       await axios.post(`/api/user/update/${id}`, updatedFields);
      
      // Then update the min bet amount separately
      try {
        const minBetResponse = await axios.post(`/api/user/updateMinBet/${id}`, { minBetAmount });
        if (!minBetResponse.data) {
          throw new Error("Failed to update minimum bet amount");
        }
      } catch (minBetError) {
        // Handle min bet error specifically
        const minBetErrMsg = minBetError.response?.data?.message || 
                           (minBetError.response?.status === 400 ? "Invalid minimum bet amount" : 
                           "Error updating minimum bet amount");
        throw new Error(`User updated but: ${minBetErrMsg}`);
      }

      setSuccess("User updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      setError(err.message || "Error updating user");
    } finally {
      setLoading(false);
    }
  }, [user, id, imageFile, minBetAmount]);

  const branchOptions = useMemo(() => {
    if (["admin", "employee"].includes(currentUser.role)) {
      return currentUser.branch?.split(',').map((branch, index) => (
        <option key={index} value={branch}>{branch}</option>
      ));
    }
    return branches.map(branch => (
      <option key={branch._id} value={branch.name}>{branch.name}</option>
    ));
  }, [currentUser, branches]);

  return (
    <div className="tw-bg-slate-100 tw-flex tw-justify-center tw-items-center tw-min-h-screen">
      <div className="tw-w-full tw-max-w-md tw-p-8 tw-rounded-md tw-shadow-md tw-border-l-8 tw-border-l-blue-600 tw-bg-white">
        <h2 className="tw-text-lg tw-font-semibold tw-mb-4">Edit User</h2>
        {error && (
          <div className="tw-mb-4 tw-p-3 tw-bg-red-100 tw-border tw-border-red-400 tw-text-red-700 tw-rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="tw-mb-4 tw-p-3 tw-bg-green-100 tw-border tw-border-green-400 tw-text-green-700 tw-rounded">
            {success}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          {[
            { id: "name", label: "Name", type: "text", required: true },
            { id: "username", label: "Username", type: "text", required: true },
            { id: "phone", label: "Phone", type: "tel", required: true },
            { id: "cut", label: "Cut", type: "number", required: true },
            { id: "password", label: "Password", type: "password", required: false }
          ].map((field) => (
            <div key={field.id} className="tw-mb-4">
              <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor={field.id}>
                {field.label}
              </label>
              <input
                className="tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                type={field.type}
                name={field.id}
                id={field.id}
                value={user[field.id] || ""}
                onChange={handleChange}
                required={field.required}
              />
            </div>
          ))}

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="branch">
              Branch
            </label>
            <select
              name="branch"
              value={user.branch || ""}
              onChange={handleChange}
              className="tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
              required
            >
              <option value="">Select Branch</option>
              {branchOptions}
            </select>
            {branchesHasMore && (
              <button
                type="button"
                onClick={fetchBranches}
                disabled={loadingBranches}
                className="tw-mt-2 tw-text-sm tw-text-blue-600 hover:tw-underline disabled:tw-opacity-50"
              >
                {loadingBranches ? "Loading more branches..." : "Load More Branches"}
              </button>
            )}
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="role">
              Role
            </label>
            <select
              name="role"
              value={user.role || "employee"}
              onChange={handleChange}
              className="tw-w-full tw-rounded-lg tw-border tw-border-slate-300 tw-p-2.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
            >
              <option value="employee">Employee</option>
              {currentUser.role === "agent" && (
                <option value="admin">Admin</option>
              )}
            </select>
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="imageUrl">
              Profile Image
            </label>
            <input
              type="file"
              id="imageUrl"
              onChange={handleFileChange}
              className="tw-w-full tw-px-3 tw-py-2 tw-rounded-md tw-border tw-border-gray-300 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
              accept="image/*"
            />
          </div>

          <div className="tw-mb-4">
            <label className="tw-block tw-text-gray-700 tw-mb-2" htmlFor="minBetAmount">
              Minimum Bet Amount: {minBetAmount}
            </label>
            <input
              type="range"
              id="minBetAmount"
              min="10"
              max="100"
              step="5"
              value={minBetAmount}
              onChange={(e) => setMinBetAmount(Number(e.target.value))}
              className="tw-w-full"
            />
            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
              Must be between 10 and 100
            </div>
          </div>

          <div className="tw-flex tw-justify-end tw-mt-4">
            <button
              type="submit"
              disabled={loading}
              className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-md hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EditUser;